import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AanmerkelijkBelang,
  BokkzIbDataService,
  IncomeTaxEntity,
  Rekening
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {optionsLandcodesMetNLD} from '../../gedeelde-componenten/gedeelde-componenten.component';
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type RekeningFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  rekeningNummer: FormControl<string | null>;
  landCode: FormControl<string | null>;
  groenRekening: FormControl<boolean | null>;
  waardeEindJaar: FormControl<number | null>;
  directRendement: FormControl<number | null>;
  valutaResultaat: FormControl<number | null>;
  werkelijkRendement: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-box3-bankrekeningen',
  templateUrl: './box3-bankrekeningen.component.html',
  styleUrls: ['./box3-bankrekeningen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiDialogModule,
    SharedUiIconModule,
  ],
})
export class Box3BankrekeningenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;
  public heeftOWR = false;
  public isDefAangifte = false;
  public dialogVisible = false;
  public index = 0;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  public formGroup = new FormGroup({
    box3Bankrekeningen: new FormArray<RekeningFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
      this.isDefAangifte = entity.taxType === 'DA';
      this.formGroup.reset({}, {emitEvent: false}); // Prevent reset from emitting events
      this.updateForm(entity?.aangifte?.box3?.bankrekeningen || []);
      this.heeftOWR = entity.aangifte?.box3?.berekenWerkelijkRendement ?? false;
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();
    this.subscribeToBedragChanges(this.box3Bankrekeningen);
  }

  private subscribeToBedragChanges(formArray: FormArray<RekeningFormGroup>): void {
    const extractBedragen = () =>
      formArray.getRawValue().map((item) => ({
        bedrag: item.bedrag,
        directRendement: item.directRendement,
      }));

    this.changeSubscriptions.add(
      formArray.valueChanges
        .pipe(
          debounceTime(500),
          map(() => this.saveForm()),
          map(() => extractBedragen()),
          startWith(extractBedragen()),
          pairwise(),
          filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.dataService.calculateLatestIncomeTaxReturn().subscribe();
        }),
    );
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  get box3Bankrekeningen(): FormArray {
    return this.formGroup.get('box3Bankrekeningen') as FormArray;
  }

  addRij(): void {
    this.box3Bankrekeningen.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.box3Bankrekeningen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
    this.subscribeToChanges();
  }

  createFormGroup(item?: Rekening): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      rekeningNummer: new FormControl<string | null>(item?.rekeningNummer ?? null),
      landCode: new FormControl<string | null>(item?.landCode ?? null),
      groenRekening: new FormControl<boolean | null>(item?.groenRekening ?? null),
      waardeEindJaar: new FormControl<number | null>(item?.waardeEindJaar ?? null),
      directRendement: new FormControl<number | null>(item?.directRendement ?? null),
      valutaResultaat: new FormControl<number | null>(item?.valutaResultaat ?? null),
      werkelijkRendement: new FormControl<number | null>(null),
    });
  }

  private updateForm(data: Rekening[]): void {
    const lijst = data || [];
    const formArray = this.box3Bankrekeningen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    const items: AanmerkelijkBelang[] = this.formGroup
      .getRawValue()
      .box3Bankrekeningen.filter(
        (item) => item.bedrag !== 0 && !(item.bedrag == null && item.omschrijving == null && item.landCode == null),
      )
      .map((item) => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
        rekeningNummer: item.rekeningNummer ?? undefined,
        landCode: item.landCode ?? undefined,
        groenRekening: item.groenRekening ?? undefined,
        waardeEindJaar: item.waardeEindJaar ?? undefined,
        directRendement: item.directRendement ?? undefined,
        valutaResultaat: item.valutaResultaat ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          box3: {
            ...currentEntity.aangifte.box3,
            bankrekeningen: items,
          },
        },
      } as IncomeTaxEntity;
    });
  }

  getOWRDialogTitel(i: number): string {
    const omschrijving = this.box3Bankrekeningen.controls[i]?.get('omschrijving')?.value;
    const rekeningnummer = this.box3Bankrekeningen.controls[i]?.get('rekeningNummer')?.value;

    if (!omschrijving || !rekeningnummer) {
      return 'Werkelijk rendement bankrekening';
    }
    return `Werkelijk rendement: ${omschrijving} - ${rekeningnummer}`;
  }

  getBerekendeWerkelijkRendement(i: number): any {
    const control = this.box3Bankrekeningen.at(i);
    const directRendement = Number(control?.get('directRendement')?.value) || 0;
    const valutaResultaat = Number(control?.get('valutaResultaat')?.value) || 0;

    if (!control?.get('directRendement')?.value && !control?.get('valutaResultaat')?.value) {
      return null;
    }
    control.patchValue({werkelijkRendement: directRendement + valutaResultaat}, {emitEvent: false});
    return directRendement + valutaResultaat;
  }
}
